import { __assign, __values, __read } from '../../ext/tslib/tslib.es6.js';
import { snapshot } from '../../../rrweb-snapshot/es/rrweb-snapshot.js';
import { mutationBuffers, initObservers } from './observer.js';
import { createMirror, polyfill, getWindowWidth, getWindowHeight, isIframeINode, hasShadowRoot, on } from '../utils.js';
import { EventType, IncrementalSource } from '../types.js';
import { IframeManager } from './iframe-manager.js';
import { ShadowDomManager } from './shadow-dom-manager.js';
import { CanvasManager } from './observers/canvas/canvas-manager.js';

function wrapEvent(e) {
  return __assign(__assign({}, e), {
    timestamp: Date.now()
  });
}

var wrappedEmit;
var takeFullSnapshot;
var mirror = createMirror();

function record(options) {
  if (options === void 0) {
    options = {};
  }

  var emit = options.emit,
      checkoutEveryNms = options.checkoutEveryNms,
      checkoutEveryNth = options.checkoutEveryNth,
      _a = options.blockClass,
      blockClass = _a === void 0 ? 'rr-block' : _a,
      _b = options.blockSelector,
      blockSelector = _b === void 0 ? null : _b,
      _c = options.ignoreClass,
      ignoreClass = _c === void 0 ? 'rr-ignore' : _c,
      _d = options.maskTextClass,
      maskTextClass = _d === void 0 ? 'rr-mask' : _d,
      _e = options.maskTextSelector,
      maskTextSelector = _e === void 0 ? null : _e,
      _f = options.inlineStylesheet,
      inlineStylesheet = _f === void 0 ? true : _f,
      maskAllInputs = options.maskAllInputs,
      _maskInputOptions = options.maskInputOptions,
      _slimDOMOptions = options.slimDOMOptions,
      maskInputFn = options.maskInputFn,
      maskTextFn = options.maskTextFn,
      hooks = options.hooks,
      packFn = options.packFn,
      _g = options.sampling,
      sampling = _g === void 0 ? {} : _g,
      mousemoveWait = options.mousemoveWait,
      _h = options.recordCanvas,
      recordCanvas = _h === void 0 ? false : _h,
      _j = options.userTriggeredOnInput,
      userTriggeredOnInput = _j === void 0 ? false : _j,
      _k = options.collectFonts,
      collectFonts = _k === void 0 ? false : _k,
      _l = options.inlineImages,
      inlineImages = _l === void 0 ? false : _l,
      plugins = options.plugins,
      _m = options.keepIframeSrcFn,
      keepIframeSrcFn = _m === void 0 ? function () {
    return false;
  } : _m;

  if (!emit) {
    throw new Error('emit function is required');
  }

  if (mousemoveWait !== undefined && sampling.mousemove === undefined) {
    sampling.mousemove = mousemoveWait;
  }

  var maskInputOptions = maskAllInputs === true ? {
    color: true,
    date: true,
    'datetime-local': true,
    email: true,
    month: true,
    number: true,
    range: true,
    search: true,
    tel: true,
    text: true,
    time: true,
    url: true,
    week: true,
    textarea: true,
    select: true,
    password: true
  } : _maskInputOptions !== undefined ? _maskInputOptions : {
    password: true
  };
  var slimDOMOptions = _slimDOMOptions === true || _slimDOMOptions === 'all' ? {
    script: true,
    comment: true,
    headFavicon: true,
    headWhitespace: true,
    headMetaSocial: true,
    headMetaRobots: true,
    headMetaHttpEquiv: true,
    headMetaVerification: true,
    headMetaAuthorship: _slimDOMOptions === 'all',
    headMetaDescKeywords: _slimDOMOptions === 'all'
  } : _slimDOMOptions ? _slimDOMOptions : {};
  polyfill();
  var lastFullSnapshotEvent;
  var incrementalSnapshotCount = 0;

  var eventProcessor = function eventProcessor(e) {
    var e_1, _a;

    try {
      for (var _b = __values(plugins || []), _c = _b.next(); !_c.done; _c = _b.next()) {
        var plugin = _c.value;

        if (plugin.eventProcessor) {
          e = plugin.eventProcessor(e);
        }
      }
    } catch (e_1_1) {
      e_1 = {
        error: e_1_1
      };
    } finally {
      try {
        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
      } finally {
        if (e_1) throw e_1.error;
      }
    }

    if (packFn) {
      e = packFn(e);
    }

    return e;
  };

  wrappedEmit = function wrappedEmit(e, isCheckout) {
    var _a;

    if (((_a = mutationBuffers[0]) === null || _a === void 0 ? void 0 : _a.isFrozen()) && e.type !== EventType.FullSnapshot && !(e.type === EventType.IncrementalSnapshot && e.data.source === IncrementalSource.Mutation)) {
      mutationBuffers.forEach(function (buf) {
        return buf.unfreeze();
      });
    }

    emit(eventProcessor(e), isCheckout);

    if (e.type === EventType.FullSnapshot) {
      lastFullSnapshotEvent = e;
      incrementalSnapshotCount = 0;
    } else if (e.type === EventType.IncrementalSnapshot) {
      if (e.data.source === IncrementalSource.Mutation && e.data.isAttachIframe) {
        return;
      }

      incrementalSnapshotCount++;
      var exceedCount = checkoutEveryNth && incrementalSnapshotCount >= checkoutEveryNth;
      var exceedTime = checkoutEveryNms && e.timestamp - lastFullSnapshotEvent.timestamp > checkoutEveryNms;

      if (exceedCount || exceedTime) {
        takeFullSnapshot(true);
      }
    }
  };

  var wrappedMutationEmit = function wrappedMutationEmit(m) {
    wrappedEmit(wrapEvent({
      type: EventType.IncrementalSnapshot,
      data: __assign({
        source: IncrementalSource.Mutation
      }, m)
    }));
  };

  var wrappedScrollEmit = function wrappedScrollEmit(p) {
    return wrappedEmit(wrapEvent({
      type: EventType.IncrementalSnapshot,
      data: __assign({
        source: IncrementalSource.Scroll
      }, p)
    }));
  };

  var wrappedCanvasMutationEmit = function wrappedCanvasMutationEmit(p) {
    return wrappedEmit(wrapEvent({
      type: EventType.IncrementalSnapshot,
      data: __assign({
        source: IncrementalSource.CanvasMutation
      }, p)
    }));
  };

  var iframeManager = new IframeManager({
    mutationCb: wrappedMutationEmit
  });
  var canvasManager = new CanvasManager({
    recordCanvas: recordCanvas,
    mutationCb: wrappedCanvasMutationEmit,
    win: window,
    blockClass: blockClass,
    mirror: mirror
  });
  var shadowDomManager = new ShadowDomManager({
    mutationCb: wrappedMutationEmit,
    scrollCb: wrappedScrollEmit,
    bypassOptions: {
      blockClass: blockClass,
      blockSelector: blockSelector,
      maskTextClass: maskTextClass,
      maskTextSelector: maskTextSelector,
      inlineStylesheet: inlineStylesheet,
      maskInputOptions: maskInputOptions,
      maskTextFn: maskTextFn,
      maskInputFn: maskInputFn,
      recordCanvas: recordCanvas,
      inlineImages: inlineImages,
      sampling: sampling,
      slimDOMOptions: slimDOMOptions,
      iframeManager: iframeManager,
      canvasManager: canvasManager
    },
    mirror: mirror
  });

  takeFullSnapshot = function takeFullSnapshot(isCheckout) {
    var _a, _b, _c, _d;

    if (isCheckout === void 0) {
      isCheckout = false;
    }

    wrappedEmit(wrapEvent({
      type: EventType.Meta,
      data: {
        href: window.location.href,
        width: getWindowWidth(),
        height: getWindowHeight()
      }
    }), isCheckout);
    mutationBuffers.forEach(function (buf) {
      return buf.lock();
    });

    var _e = __read(snapshot(document, {
      blockClass: blockClass,
      blockSelector: blockSelector,
      maskTextClass: maskTextClass,
      maskTextSelector: maskTextSelector,
      inlineStylesheet: inlineStylesheet,
      maskAllInputs: maskInputOptions,
      maskTextFn: maskTextFn,
      slimDOM: slimDOMOptions,
      recordCanvas: recordCanvas,
      inlineImages: inlineImages,
      onSerialize: function onSerialize(n) {
        if (isIframeINode(n)) {
          iframeManager.addIframe(n);
        }

        if (hasShadowRoot(n)) {
          shadowDomManager.addShadowRoot(n.shadowRoot, document);
        }
      },
      onIframeLoad: function onIframeLoad(iframe, childSn) {
        iframeManager.attachIframe(iframe, childSn);
        shadowDomManager.observeAttachShadow(iframe);
      },
      keepIframeSrcFn: keepIframeSrcFn
    }), 2),
        node = _e[0],
        idNodeMap = _e[1];

    if (!node) {
      return console.warn('Failed to snapshot the document');
    }

    mirror.map = idNodeMap;
    wrappedEmit(wrapEvent({
      type: EventType.FullSnapshot,
      data: {
        node: node,
        initialOffset: {
          left: window.pageXOffset !== undefined ? window.pageXOffset : (document === null || document === void 0 ? void 0 : document.documentElement.scrollLeft) || ((_b = (_a = document === null || document === void 0 ? void 0 : document.body) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.scrollLeft) || (document === null || document === void 0 ? void 0 : document.body.scrollLeft) || 0,
          top: window.pageYOffset !== undefined ? window.pageYOffset : (document === null || document === void 0 ? void 0 : document.documentElement.scrollTop) || ((_d = (_c = document === null || document === void 0 ? void 0 : document.body) === null || _c === void 0 ? void 0 : _c.parentElement) === null || _d === void 0 ? void 0 : _d.scrollTop) || (document === null || document === void 0 ? void 0 : document.body.scrollTop) || 0
        }
      }
    }));
    mutationBuffers.forEach(function (buf) {
      return buf.unlock();
    });
  };

  try {
    var handlers_1 = [];
    handlers_1.push(on('DOMContentLoaded', function () {
      wrappedEmit(wrapEvent({
        type: EventType.DomContentLoaded,
        data: {}
      }));
    }));

    var observe_1 = function observe_1(doc) {
      var _a;

      return initObservers({
        mutationCb: wrappedMutationEmit,
        mousemoveCb: function mousemoveCb(positions, source) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: {
              source: source,
              positions: positions
            }
          }));
        },
        mouseInteractionCb: function mouseInteractionCb(d) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.MouseInteraction
            }, d)
          }));
        },
        scrollCb: wrappedScrollEmit,
        viewportResizeCb: function viewportResizeCb(d) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.ViewportResize
            }, d)
          }));
        },
        inputCb: function inputCb(v) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.Input
            }, v)
          }));
        },
        mediaInteractionCb: function mediaInteractionCb(p) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.MediaInteraction
            }, p)
          }));
        },
        styleSheetRuleCb: function styleSheetRuleCb(r) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.StyleSheetRule
            }, r)
          }));
        },
        styleDeclarationCb: function styleDeclarationCb(r) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.StyleDeclaration
            }, r)
          }));
        },
        canvasMutationCb: wrappedCanvasMutationEmit,
        fontCb: function fontCb(p) {
          return wrappedEmit(wrapEvent({
            type: EventType.IncrementalSnapshot,
            data: __assign({
              source: IncrementalSource.Font
            }, p)
          }));
        },
        blockClass: blockClass,
        ignoreClass: ignoreClass,
        maskTextClass: maskTextClass,
        maskTextSelector: maskTextSelector,
        maskInputOptions: maskInputOptions,
        inlineStylesheet: inlineStylesheet,
        sampling: sampling,
        recordCanvas: recordCanvas,
        inlineImages: inlineImages,
        userTriggeredOnInput: userTriggeredOnInput,
        collectFonts: collectFonts,
        doc: doc,
        maskInputFn: maskInputFn,
        maskTextFn: maskTextFn,
        blockSelector: blockSelector,
        slimDOMOptions: slimDOMOptions,
        mirror: mirror,
        iframeManager: iframeManager,
        shadowDomManager: shadowDomManager,
        canvasManager: canvasManager,
        plugins: ((_a = plugins === null || plugins === void 0 ? void 0 : plugins.filter(function (p) {
          return p.observer;
        })) === null || _a === void 0 ? void 0 : _a.map(function (p) {
          return {
            observer: p.observer,
            options: p.options,
            callback: function callback(payload) {
              return wrappedEmit(wrapEvent({
                type: EventType.Plugin,
                data: {
                  plugin: p.name,
                  payload: payload
                }
              }));
            }
          };
        })) || []
      }, hooks);
    };

    iframeManager.addLoadListener(function (iframeEl) {
      try {
        handlers_1.push(observe_1(iframeEl.contentDocument));
      } catch (error) {
        console.warn('error in rrweb iframe observer', error);
      }
    });

    var init_1 = function init_1() {
      takeFullSnapshot();
      handlers_1.push(observe_1(document));
    };

    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      init_1();
    } else {
      handlers_1.push(on('load', function () {
        wrappedEmit(wrapEvent({
          type: EventType.Load,
          data: {}
        }));
        init_1();
      }, window));
    }

    return function () {
      handlers_1.forEach(function (h) {
        return h();
      });
    };
  } catch (error) {
    console.warn(error);
  }
}

record.addCustomEvent = function (tag, payload) {
  if (!wrappedEmit) {
    throw new Error('please add custom event after start recording');
  }

  wrappedEmit(wrapEvent({
    type: EventType.Custom,
    data: {
      tag: tag,
      payload: payload
    }
  }));
};

record.freezePage = function () {
  mutationBuffers.forEach(function (buf) {
    return buf.freeze();
  });
};

record.takeFullSnapshot = function (isCheckout) {
  if (!takeFullSnapshot) {
    throw new Error('please take full snapshot after start recording');
  }

  takeFullSnapshot(isCheckout);
};

record.mirror = mirror;
export { record as default };